// pad-drillout-form.component.ts
import { Component, ChangeDetectionStrategy, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { DrilloutFilterService, DrillOutRow } from './home-page-filter-form-service';
import { combineLatest, Subscription, Observable } from 'rxjs';
import { startWith, map, distinctUntilChanged, tap, debounceTime, shareReplay, take } from 'rxjs/operators';

@Component({
    selector: 'app-pad-drillout-form',
    templateUrl: './drill-out-filter-form-component.html',
    styleUrls: ['./drill-out-filter-form-component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PadDrilloutFormComponent implements OnInit, OnDestroy {
    @Output() filtersChange = new EventEmitter<{}>();

    private sub = new Subscription();
    form = this.fb.group({
        padSite: [null, Validators.required],
        drillOutTemplateId: [{ value: null, disabled: true }, Validators.required],
        startDate: [null],
        endDate: [null]
    });

    all$ = this.svc.getAll().pipe(shareReplay(1));

    pads$ = this.all$.pipe(
        map(rows => {
            const m = new Map<string, string>();
            rows.forEach(r => {
                if (r.padSite && !m.has(r.padSite)) m.set(r.padSite, r.padSite);
            });
            return Array.from(m, ([id, name]) => ({ id, name }));
        })
    );

    wellsForPad$ = combineLatest([
        this.all$,
        this.form.get('padSite')!.valueChanges.pipe(startWith(this.form.get('padSite')!.value))
    ]).pipe(
        map(([rows, pad]) => {
            const list = pad ? rows.filter(r => r.padSite === pad) : [];
            const siteCtrl = this.form.get('drillOutTemplateId')!;

            if (list.length) {
                siteCtrl.enable({ emitEvent: false });
            } else {
                siteCtrl.disable({ emitEvent: false });
                siteCtrl.reset(null, { emitEvent: false });
                this.emitFilters();
            }

            return list;
        })
    );

    constructor(private fb: FormBuilder, private svc: DrilloutFilterService) { }

    async ngOnInit(): Promise<void> {
        this.sub.add(
            this.all$.pipe(take(1)).subscribe(rows => {
                if (rows.length > 0) {
                    const firstRow = rows[0];
                    const startDate = new Date(firstRow.startDateTime);
                    const endDate = new Date(firstRow.startDateTime);
                    endDate.setDate(endDate.getDate() + 5);
                    
                    this.form.patchValue({
                        padSite: firstRow.padSite,
                        drillOutTemplateId: firstRow.id,
                        startDate: startDate,
                        endDate: endDate
                    }, { emitEvent: true });
                }
            })
        );

        this.sub.add(
            combineLatest([
                this.all$,
                this.form.get('drillOutTemplateId')!.valueChanges
            ]).pipe(
                map(([rows, templateId]) => {
                    if (!templateId) return null;
                    return rows.find(r => r.id === templateId);
                })
            ).subscribe(selectedTemplate => {
                if (selectedTemplate && selectedTemplate.startDateTime) {
                    const startDate = new Date(selectedTemplate.startDateTime);
                    const endDate = new Date(selectedTemplate.startDateTime);
                    endDate.setDate(endDate.getDate() + 5);
                    
                    this.form.patchValue({
                        startDate: startDate,
                        endDate: endDate
                    }, { emitEvent: true });
                }
            })
        );

        this.sub.add(
            combineLatest([
                this.form.get('drillOutTemplateId')!.valueChanges.pipe(startWith(null)),
                this.form.get('startDate')!.valueChanges.pipe(startWith(null)),
                this.form.get('endDate')!.valueChanges.pipe(startWith(null))
            ]).pipe(
                debounceTime(300),
                distinctUntilChanged((prev, curr) => {
                    const [prevId, prevStart, prevEnd] = prev;
                    const [currId, currStart, currEnd] = curr;

                    const idChanged = prevId !== currId;

                    const datesChanged = 
                        (currStart && currEnd) && 
                        (!prevStart || !prevEnd || 
                        prevStart.valueOf() !== currStart.valueOf() || 
                        prevEnd.valueOf() !== currEnd.valueOf());

                    return !idChanged && !datesChanged;
                })
            ).subscribe(() => this.emitFilters())
        );
    }

    private emitFilters(): void {
        const id = this.form.get('drillOutTemplateId')!.value;
        const startDate = this.form.get('startDate')!.value;
        const endDate = this.form.get('endDate')!.value;

        if (!id) {
            this.filtersChange.emit(null);
            return;
        }

        this.filtersChange.emit({
            drillOutTemplateId: id,
            startDate: startDate,
            endDate: endDate
        });
    }

    ngOnDestroy(): void {
        this.sub.unsubscribe();
    }
}